/**
 * Sign-in security: who is locked out right now, who is emailed when it happens, and the log of every
 * refused sign-in on both identity spaces.
 *
 * The port of `Views/SecurityPage.xaml` + `SecurityViewModel` and its three card view models. The
 * policy's NUMBERS stay on Settings; this is the operational half - the thing you go and look at.
 *
 *   * ADMIN-ONLY. Every endpoint behind the page refuses a technician, so the sidebar hides the entry,
 *     and a deep link renders a sentence saying so instead of three empty cards.
 *   * THE LOCKOUT CARD HIDES WHEN NOTHING IS LOCKED - unless it has something to say. Releasing the
 *     last lockout would otherwise take the card away with the confirmation the click produced, and a
 *     success would look exactly like a button that did nothing.
 *   * A release removes the row in place rather than reloading the list, which would discard that
 *     confirmation too, and reports the new count to the sidebar at once (`lockoutCount.ts`).
 *   * "HOW MUCH LONGER", NOT "UNTIL WHEN": an absolute time has to be rendered in some timezone and is
 *     read as one in another; a duration cannot be misread.
 *   * THE FAILED SIGN-IN CARD IS SHOWN EVEN WHEN EMPTY - it is a standing security surface, and the
 *     count line says what is NOT on screen, because fifty rows with no total read as a quiet week
 *     during a spray that wrote nine thousand.
 *   * THE ALERT ADDRESS IS ALWAYS SHOWN, since it is switched on while nothing has happened yet - and
 *     says in red when the mail account cannot deliver to it, because an alert that cannot be sent
 *     reads as covered and is not.
 */
import type { Container } from 'webwin';
import { isAdmin } from '../api/auth.js';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import type { LockedAccountDto, SecurityAlertDto, SignInFailureDto } from '../contracts.gen.js';
import { localDateTime, localTime, plural } from '../format.js';
import { reportLockoutCount } from '../lockoutCount.js';
import { CardList } from '../ui/CardList.js';
import type { Card } from '../ui/Card.js';
import { chip } from '../ui/Chip.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  field,
  fillWith,
  FONT,
  GAP,
  h1,
  h2,
  muted,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';

const FAILURE_PAGE = 50;
const FAILURE_WINDOW_HOURS = 24;

/** "1 hour 20 minutes left" - how much longer, never a clock time. */
export function remainingText(lockedUntilUtc: string, now = Date.now()): string {
  const ms = Date.parse(lockedUntilUtc) - now;
  if (!Number.isFinite(ms) || ms <= 0) return 'Expiring now';
  if (ms < 60_000) return 'Less than a minute left';
  const minutes = Math.ceil(ms / 60_000);
  if (minutes < 60) return `${minutes} minutes left`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${hours === 1 ? '1 hour' : `${hours} hours`}${rest === 0 ? '' : ` ${rest} minutes`} left`;
}

export function securityPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      const { view, content } = scrollPage();
      host.controls.add(view);

      const header = stackOf(
        vstack({ spacing: GAP.sm }),
        h1('Security'),
        muted('Who is locked out, who is told when it happens, and every refused sign-in on staff and customer-portal accounts.')
      );
      content.controls.add(header);

      if (!isAdmin()) {
        // A deep link is a real route, so it answers truthfully rather than with three empty cards.
        const notice = card();
        notice.controls.add(
          text(
            'Sign-in security is administrator-only. The policy itself - how many failed attempts lock an account, for how long, and how long the log is kept - is on the Settings page, which every staff user can read.'
          )
        );
        content.controls.add(notice);
        content.controls.add(stackOf(buttonRow(), button('Sign-in policy (Settings)', () => ctx.go('/settings'))));
        relayout(content);
        return;
      }

      const pageStatus = new StatusLine();
      // Under the title, not beside it: a wrapping row beside the title is measured to one button's width.
      header.controls.add(
        stackOf(
          buttonRow(),
          button('Refresh', () => void loadAll()),
          button('Sign-in policy (Settings)', () => ctx.go('/settings')),
          button('Audit log', () => ctx.go('/audit'))
        )
      );
      header.controls.add(pageStatus.label);

      // =================================================================
      //  Locked out
      // =================================================================

      let locked: LockedAccountDto[] = [];
      const lockStatus = new StatusLine();
      const lockList = new CardList<LockedAccountDto>({
        key: l => `${l.portal ? 'p' : 's'}:${l.email}`,
        build: l => buildLockRow(l),
        spacing: GAP.sm
      });
      const lockCard = card({ tint: 'warn', visible: false });
      lockCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Locked out'),
          muted('Each address below refuses every password - including the right one - until its time runs out or you release it here.'),
          lockList.panel,
          lockStatus.label
        )
      );

      function buildLockRow(l: LockedAccountDto): Card {
        const heading = buttonRow({ spacing: GAP.sm });
        heading.controls.add(value(l.email, { font: FONT.bold }));
        heading.controls.add(chip(l.portal ? 'Customer portal' : 'Staff', 'neutral'));

        // An address nobody has is the ordinary shape of a bot working through a list - worth telling
        // apart from a colleague who has forgotten their password.
        const who = l.displayName?.trim() ? l.displayName : 'No account with this address';
        const releaseButton = button('Release', () => void release(l, releaseButton), true);

        const row = splitRow({ align: 'center' });
        fillWith(
          row,
          stackOf(
            vstack({ spacing: GAP.xs }),
            heading,
            muted(who),
            text(`${plural(l.failedAttempts, 'failed attempt')}, last at ${localTime(l.lastFailureUtc)} · ${remainingText(l.lockedUntilUtc)}`, {
              font: FONT.small
            })
          )
        );
        row.controls.add(releaseButton);
        const shell = card();
        shell.controls.add(row);
        return shell;
      }

      function showLocks(): void {
        lockList.set(locked);
        lockCard.visible = locked.length > 0 || lockStatus.text.length > 0;
        relayout(lockCard);
      }

      async function release(l: LockedAccountDto, b: { text: string; enabled: boolean }): Promise<void> {
        if (!b.enabled) return;
        b.enabled = false;
        b.text = 'Releasing…';
        try {
          await crm.unlockAccount(l.email, l.portal);
          if (ctx.cancelled()) return;
          lockStatus.ok(`${l.email} can sign in again. There is nothing more to do here.`);
          locked = locked.filter(x => x !== l);
          reportLockoutCount(locked.length);
          showLocks();
          // Re-read the log below: what this address was doing is the question a release came from.
          void loadFailures();
        } catch (error) {
          if (ctx.cancelled()) return;
          b.enabled = true;
          b.text = 'Release';
          lockStatus.error(describeError(error));
          showLocks();
        }
      }

      async function loadLocks(): Promise<void> {
        lockStatus.clear();
        locked = [...(await crm.getLockedAccounts(ctx.dispose.signal()))].sort(
          (a, b) => Date.parse(a.lockedUntilUtc) - Date.parse(b.lockedUntilUtc)
        );
        if (ctx.cancelled()) return;
        reportLockoutCount(locked.length);
        showLocks();
      }

      // =================================================================
      //  Lockout alerts
      // =================================================================

      const alertBox = textBox({ placeholder: 'someone@yourcompany.com, another@yourcompany.com' });
      const alertStatus = new StatusLine();
      const deliveryWarning = muted('', 'danger');
      const lastAlert = muted('');
      const alertCard = card();
      alertCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Lockout alerts'),
          muted(
            'Emailed when an account is locked out - to the addresses here, never to the address that was locked out. Several inside fifteen minutes arrive as one summary. Leave it blank to switch alerts off.'
          ),
          field('Send alerts to', alertBox),
          deliveryWarning,
          lastAlert,
          stackOf(buttonRow(), button('Save', () => void saveAlerts(), true)),
          alertStatus.label
        )
      );

      function showAlerts(a: SecurityAlertDto): void {
        alertBox.text = a.notifyEmail ?? '';
        deliveryWarning.text =
          !a.deliverable && (a.notifyEmail ?? '').trim()
            ? 'This address cannot be alerted: the instance mail account is not configured to send. Set it up on the Settings page, or clear this box so it does not read as covered.'
            : '';
        deliveryWarning.visible = deliveryWarning.text.length > 0;
        lastAlert.text = a.lastAlertUtc ? `Last alert sent ${localDateTime(a.lastAlertUtc)}.` : '';
        lastAlert.visible = lastAlert.text.length > 0;
        relayout(alertCard);
      }

      async function loadAlerts(): Promise<void> {
        const a = await crm.getSecurityAlerts(ctx.dispose.signal());
        if (!ctx.cancelled()) showAlerts(a);
      }

      async function saveAlerts(): Promise<void> {
        alertStatus.busy('Saving…');
        try {
          const saved = await crm.saveSecurityAlerts(alertBox.text);
          if (ctx.cancelled()) return;
          // Re-bound to what the server stored, which trims - a box still showing something else would
          // describe a configuration nothing is using.
          showAlerts(saved);
          alertStatus.ok(
            (saved.notifyEmail ?? '').trim()
              ? 'Saved. An email goes out the next time an account is locked out.'
              : 'Lockout alerts are off.'
          );
        } catch (error) {
          if (!ctx.cancelled()) alertStatus.error(describeError(error));
        }
      }

      // =================================================================
      //  Failed sign-ins
      // =================================================================

      const filterBox = textBox({ placeholder: 'Narrow to one address' });
      filterBox.on('keydown', (e: { key: string }) => {
        if (e.key === 'Enter') void loadFailures();
      });
      const failureSummary = text('');
      const failureEmpty = muted('');
      const failureList = new CardList<SignInFailureDto>({ key: f => f.id, build: f => buildFailureRow(f), spacing: GAP.xs });
      const failureCard = card();
      failureCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Failed sign-ins'),
          muted('Every refused sign-in on either identity space, newest first. Nothing clears this log; it ages out on its own.'),
          stackOf(
            buttonRow({ spacing: GAP.md, align: 'end' }),
            field('Address', filterBox, { width: 260 }),
            button('Search', () => void loadFailures(), true),
            button('Show all', () => {
              filterBox.text = '';
              void loadFailures();
            })
          ),
          failureSummary,
          failureEmpty,
          failureList.panel
        )
      );

      function buildFailureRow(f: SignInFailureDto): Card {
        const heading = buttonRow({ spacing: GAP.sm });
        heading.controls.add(value(f.email?.trim() ? f.email : '(no address)', { font: FONT.bold }));
        heading.controls.add(value(localDateTime(f.whenUtc), { font: FONT.small, role: 'muted' }));
        if (f.causedLockout) heading.controls.add(chip('this one locked the account', 'danger'));

        // The space is on every row: one address can exist in both, and a refusal that does not say
        // which sign-in it was is ambiguous exactly when it matters.
        const detail = [
          f.portal ? 'Customer portal' : 'Staff',
          f.reasonText,
          `from ${f.ipAddress?.trim() ? f.ipAddress : 'an unknown address'}`
        ].join(' - ');
        const shell = card({ tint: 'alt' });
        shell.controls.add(stackOf(vstack({ spacing: 0 }), heading, muted(detail)));
        return shell;
      }

      async function loadFailures(): Promise<void> {
        const filter = filterBox.text.trim();
        try {
          const result = await crm.getSignInFailures(filter, FAILURE_PAGE, FAILURE_WINDOW_HOURS, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          failureList.set(result.failures);
          const scope = filter ? ` for ${filter}` : '';
          const shown = result.failures.length;
          failureSummary.text =
            result.totalStored === 0
              ? ''
              : `${result.inWindow} in the last ${FAILURE_WINDOW_HOURS} hours${scope}; ${result.totalStored} recorded in total` +
                (shown < result.totalStored ? `, showing the most recent ${shown}.` : '.');
          failureSummary.visible = failureSummary.text.length > 0;
          failureEmpty.text = shown > 0 ? '' : filter ? `No failed sign-ins recorded for ${filter}.` : 'No failed sign-ins recorded.';
          failureEmpty.visible = failureEmpty.text.length > 0;
          relayout(failureCard);
        } catch (error) {
          if (!ctx.cancelled()) pageStatus.error('Could not load the failed sign-ins: ' + describeError(error));
        }
      }

      content.controls.add(stackOf(vstack({ spacing: GAP.lg }), lockCard, alertCard, failureCard));

      async function loadAll(): Promise<void> {
        pageStatus.clear();
        try {
          await Promise.all([loadLocks(), loadAlerts(), loadFailures()]);
        } catch (error) {
          if (!ctx.cancelled()) pageStatus.error('Could not load the security page: ' + describeError(error));
        }
        relayout(content);
      }

      void loadAll();
    }
  };
}
